import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Avatar
} from '@mui/material';
import {
  FamilyRestroom as FamilyIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { familyApi } from '../api/apiService';
import { useAuth } from '../context/AuthContext';

const JoinFamilyPage: React.FC = () => {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, refreshKids } = useAuth();

  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteInfo, setInviteInfo] = useState<{
    valid: boolean;
    familyName?: string;
    inviterName?: string;
  } | null>(null);

  useEffect(() => {
    if (inviteCode) {
      // Store invite code in case user needs to login
      sessionStorage.setItem('pendingInviteCode', inviteCode);
      validateInvite(inviteCode);
    }
  }, [inviteCode]);

  const validateInvite = async (code: string) => {
    try {
      setLoading(true);
      const response = await familyApi.validateInviteCode(code);
      setInviteInfo(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid invite code');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinFamily = async () => {
    if (!inviteCode) return;

    try {
      setJoining(true);
      setError(null);
      await familyApi.joinFamily(inviteCode);
      sessionStorage.removeItem('pendingInviteCode');
      await refreshKids();
      navigate('/select-kid');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to join family');
    } finally {
      setJoining(false);
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Not logged in - prompt to login
    return (
      <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
        <Card sx={{ borderRadius: 3, textAlign: 'center', py: 4 }}>
          <CardContent>
            <FamilyIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Join a Family
            </Typography>
            {inviteInfo?.valid ? (
              <>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  <strong>{inviteInfo.inviterName}</strong> invites you to join their family.
                  Please log in to accept this invitation.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleLogin}
                >
                  Log In to Accept
                </Button>
              </>
            ) : (
              <Alert severity="error">
                {error || 'Invalid invite code'}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {inviteInfo?.valid && (
        <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
          <Box
            sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              py: 4,
              px: 3
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'rgba(255,255,255,0.2)',
                mx: 'auto',
                mb: 2
              }}
            >
              <FamilyIcon sx={{ fontSize: 40, color: 'white' }} />
            </Avatar>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
              You're Invited!
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              {inviteInfo.inviterName} invites you to join their family
            </Typography>
          </Box>
          <CardContent sx={{ py: 4 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              By joining this family, you'll be able to:
            </Typography>
            <Box sx={{ textAlign: 'left', mb: 3 }}>
              {[
                'View and manage all kids in the family',
                'Create and complete targets for any kid',
                'Add and manage gifts for all kids',
                'See family statistics and progress'
              ].map((benefit, index) => (
                <Box
                  key={index}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  <Typography variant="body2">{benefit}</Typography>
                </Box>
              ))}
            </Box>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleJoinFamily}
              disabled={joining}
              sx={{ py: 1.5 }}
            >
              {joining ? <CircularProgress size={24} color="inherit" /> : 'Join Family'}
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default JoinFamilyPage;